import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { matchesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const logoPath = (shortName) => `/assets/assets/${shortName?.toLowerCase()}.png`;

export default function MatchDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    matchesAPI.getById(id)
      .then(res => { setMatch(res.data); setSelectedCategory(res.data.ticketCategories?.[0]); })
      .catch(() => toast.error('Match not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleBook = () => {
    if (!user) { navigate('/login'); return; }
    navigate(`/matches/${id}/seats`, { state: { category: selectedCategory, quantity } });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#09090f' }}>
      <div style={{ width: 42, height: 42, borderRadius: '50%', border: '3px solid rgba(255,107,0,0.15)', borderTopColor: '#ff6b00', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!match) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.4)', background: '#09090f', minHeight: '100vh' }}>
      Match not found.
    </div>
  );

  const { team1, team2, venue, date, time, matchType, ticketCategories } = match;
  const isFinal = matchType === 'final';
  const c1 = team1?.color || '#ff6b00';
  const c2 = team2?.color || '#ffd700';
  const subtotal   = (selectedCategory?.price || 0) * quantity;
  const fee        = Math.round(subtotal * 0.02);
  const grandTotal = subtotal + fee;

  return (
    <div style={{ background: '#09090f', minHeight: '100vh', paddingTop: 'var(--nav-h, 68px)', color: '#f0f0f8', fontFamily: 'Outfit, sans-serif' }}>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes fadeUp{ from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }
        .cat-row         { transition: all 0.15s; cursor: pointer; }
        .cat-row:hover   { transform: translateX(3px); }
        .book-btn        { transition: box-shadow 0.18s, transform 0.18s; }
        .book-btn:hover  { box-shadow: 0 10px 36px rgba(255,107,0,0.55) !important; transform: translateY(-1px); }
        @media(max-width: 800px){
          .detail-grid   { flex-direction: column !important; }
          .summary-panel { position: static !important; width: 100% !important; }
        }
      `}</style>

      {/* ── Hero banner ── */}
      <div style={{
        background: `linear-gradient(135deg, ${c1}18 0%, #0c0c18 45%, ${c2}18 100%)`,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '36px 0 32px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>

          {/* Back + badge row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <Link to="/matches" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontFamily: 'Rajdhani, sans-serif', fontSize: 13, letterSpacing: '0.05em' }}>
              ← Back to Matches
            </Link>
            <span style={{ background: '#ff6b00', color: '#fff', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 11, padding: '3px 10px', borderRadius: 20, letterSpacing: '0.08em' }}>
              MATCH {match.matchNumber}
            </span>
            {isFinal && (
              <span style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.3)', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 11, padding: '3px 10px', borderRadius: 20, letterSpacing: '0.08em' }}>
                ⭐ FINAL
              </span>
            )}
          </div>

          {/* Teams VS row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 32, marginBottom: 32, flexWrap: 'wrap' }}>
            {/* Team 1 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: `${c1}18`, border: `2px solid ${c1}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={logoPath(team1.shortName)} alt={team1.shortName} width={48} height={48} style={{ objectFit: 'contain' }} onError={e => e.target.style.opacity = 0} />
              </div>
              <div>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 44, color: c1, lineHeight: 1, letterSpacing: '0.03em' }}>{team1.shortName}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: 'Rajdhani, sans-serif', marginTop: 2 }}>{team1.name}</div>
              </div>
            </div>

            {/* VS */}
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 42, color: '#ff6b00', letterSpacing: '0.04em', opacity: 0.9 }}>VS</div>

            {/* Team 2 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: `${c2}18`, border: `2px solid ${c2}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={logoPath(team2.shortName)} alt={team2.shortName} width={48} height={48} style={{ objectFit: 'contain' }} onError={e => e.target.style.opacity = 0} />
              </div>
              <div>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 44, color: c2, lineHeight: 1, letterSpacing: '0.03em' }}>{team2.shortName}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: 'Rajdhani, sans-serif', marginTop: 2 }}>{team2.name}</div>
              </div>
            </div>
          </div>

          {/* Match meta info pills */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { icon: '📅', val: date ? format(new Date(date), 'EEE, dd MMM yyyy') : '—' },
              { icon: '🕐', val: time },
              { icon: '🏟️', val: venue.name },
              { icon: '📍', val: venue.city },
              { icon: '👥', val: `${(venue.capacity / 1000).toFixed(0)}K seats` },
            ].map(({ icon, val }) => (
              <div key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '6px 14px' }}>
                <span style={{ fontSize: 13 }}>{icon}</span>
                <span style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Ticket selection ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 20px 80px' }}>
        <div className="detail-grid" style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>

          {/* ── LEFT: category list ── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 26, letterSpacing: '0.06em', lineHeight: 1 }}>
                SELECT <span style={{ color: '#ff6b00' }}>CATEGORY</span>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', marginTop: 4 }}>
                Choose your stand — seats are auto-assigned
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ticketCategories?.map((cat, i) => {
                const isSel = selectedCategory?.name === cat.name;
                const pct   = Math.round((cat.availableSeats / (cat.totalSeats || cat.availableSeats + 1000)) * 100);
                const low   = pct < 20;
                const col   = cat.color || '#ff6b00';

                return (
                  <div
                    key={cat.name}
                    className="cat-row"
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      padding: '16px 20px',
                      background: isSel ? `${col}12` : 'rgba(255,255,255,0.025)',
                      border: `1.5px solid ${isSel ? col : 'rgba(255,255,255,0.07)'}`,
                      borderRadius: 14,
                      position: 'relative', overflow: 'hidden',
                    }}
                  >
                    {/* left accent bar */}
                    {isSel && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: col, borderRadius: '3px 0 0 3px' }} />}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      {/* Radio + name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSel ? col : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {isSel && <div style={{ width: 10, height: 10, borderRadius: '50%', background: col }} />}
                        </div>
                        <div>
                          <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 16, color: isSel ? '#fff' : 'rgba(255,255,255,0.75)', letterSpacing: '0.02em' }}>
                            {cat.name}
                          </div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', marginTop: 2, display: 'flex', gap: 10 }}>
                            <span>{cat.availableSeats?.toLocaleString()} available</span>
                            {low && <span style={{ color: '#ff6b00' }}>⚡ Filling fast</span>}
                          </div>
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 26, color: isSel ? col : 'rgba(255,255,255,0.7)', letterSpacing: '0.04em', lineHeight: 1 }}>
                          ₹{cat.price?.toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontFamily: 'Rajdhani, sans-serif' }}>per ticket</div>
                      </div>
                    </div>

                    {/* Availability bar */}
                    <div style={{ marginTop: 12, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: low ? '#ff6b00' : col, borderRadius: 2, transition: 'width 0.4s', opacity: isSel ? 0.9 : 0.45 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT: booking summary ── */}
          <div className="summary-panel" style={{ width: 320, flexShrink: 0, position: 'sticky', top: 88 }}>
            <div style={{ background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>

              {/* Header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,107,0,0.04)' }}>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 18, letterSpacing: '0.06em' }}>
                  BOOKING <span style={{ color: '#ff6b00' }}>SUMMARY</span>
                </div>
              </div>

              <div style={{ padding: '20px' }}>
                {selectedCategory ? (
                  <div style={{ marginBottom: 20, padding: '14px 16px', background: `${selectedCategory.color || '#ff6b00'}12`, border: `1px solid ${selectedCategory.color || '#ff6b00'}30`, borderRadius: 12, animation: 'fadeUp 0.2s ease' }}>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 15, color: selectedCategory.color || '#ff6b00', marginBottom: 4 }}>
                      {selectedCategory.name}
                    </div>
                    <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 32, color: '#ffd700', lineHeight: 1 }}>
                      ₹{selectedCategory.price?.toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', marginTop: 2 }}>per ticket</div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 20, padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>🎟️</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif' }}>Select a category</div>
                  </div>
                )}

                {/* Quantity */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                    Number of Tickets
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      style={{ width: 44, height: 44, background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 20, cursor: 'pointer', fontFamily: 'Rajdhani, sans-serif', flexShrink: 0 }}
                    >−</button>
                    <div style={{ flex: 1, textAlign: 'center', fontFamily: 'Bebas Neue, sans-serif', fontSize: 26, color: '#fff', letterSpacing: '0.04em' }}>{quantity}</div>
                    <button
                      onClick={() => setQuantity(Math.min(6, quantity + 1))}
                      style={{ width: 44, height: 44, background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 20, cursor: 'pointer', fontFamily: 'Rajdhani, sans-serif', flexShrink: 0 }}
                    >+</button>
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontFamily: 'Rajdhani, sans-serif', marginTop: 6, textAlign: 'center' }}>Max 6 per booking</div>
                </div>

                {/* Price breakdown */}
                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 14 }} />

                {selectedCategory ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)', fontFamily: 'Rajdhani, sans-serif' }}>
                      <span>{quantity} × ₹{selectedCategory.price?.toLocaleString()}</span>
                      <span>₹{subtotal.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif' }}>
                      <span>Convenience fee (2%)</span>
                      <span>₹{fee.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: 22, fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 20 }}>
                      <span style={{ color: 'rgba(255,255,255,0.6)' }}>Total</span>
                      <span style={{ color: '#ffd700' }}>₹{grandTotal.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ marginBottom: 22, fontSize: 13, color: 'rgba(255,255,255,0.2)', fontFamily: 'Rajdhani, sans-serif', textAlign: 'center' }}>
                    Select a category to see price
                  </div>
                )}

                {/* CTA button */}
                <button
                  className="book-btn"
                  onClick={handleBook}
                  disabled={!selectedCategory}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                    background: selectedCategory ? 'linear-gradient(135deg,#ff6b00,#ff8c38)' : 'rgba(255,255,255,0.05)',
                    color: selectedCategory ? '#fff' : 'rgba(255,255,255,0.2)',
                    fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 15,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    cursor: selectedCategory ? 'pointer' : 'not-allowed',
                    boxShadow: selectedCategory ? '0 6px 24px rgba(255,107,0,0.4)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  🎟️ Select Seats
                </button>

                {!user && (
                  <p style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 10, fontFamily: 'Rajdhani, sans-serif' }}>
                    You'll need to{' '}
                    <Link to="/login" style={{ color: '#ff6b00', textDecoration: 'none' }}>log in</Link>{' '}
                    to book
                  </p>
                )}
              </div>
            </div>

            {/* Venue card */}
            <div style={{ marginTop: 14, padding: '16px 18px', background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Venue Info</div>
              {[
                ['Stadium', venue.name],
                ['City',    venue.city],
                ['Date',    date ? format(new Date(date), 'dd MMM yyyy') : '—'],
                ['Time',    time],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12 }}>
                  <span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif' }}>{label}</span>
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}